import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  DropoffType,
  MAX_SEARCH_DRIVER_AGE,
  MIN_SEARCH_DRIVER_AGE,
} from "@apac/components/cars";
import type { Suggestion } from "@b2bportal/ground-api";
import {
  CarNavigationUtility,
  CARS_AVAILABILITY_PATH,
} from "../../../../utilities";
import {
  CarsAvailabilityActions,
  CarsAvailabilitySelectors,
} from "../../store/";

const isDriverAgeValid = (age: number) =>
  !isNaN(age) && age >= MIN_SEARCH_DRIVER_AGE && age <= MAX_SEARCH_DRIVER_AGE;

export enum CarsSearchErrors {
  pickupRequired = "pickupRequired",
  dropoffReqired = "dropoffReqired",
  pickupTimeRequired = "pickupTimeRequired",
  dropoffTimeRequired = "dropoffTimeRequired",
  ageRequired = "ageRequired",
  invalidAge = "invalidAge",
}

export const useCarSearch = (
  onSubmit?: (event?: FormEvent<HTMLFormElement>) => void
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ageError, setAgeError] = useState<string>();
  const [validationErrors, setValidationErrors] = useState<CarsSearchErrors[]>(
    []
  );
  const pickupDateTime = useSelector(
    CarsAvailabilitySelectors.getSearchFormPickupDateTime
  );
  const pickupLocation = useSelector(
    CarsAvailabilitySelectors.getSearchFormPickupLocation
  );
  const dropoffType = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffType
  );
  const dropoffDateTime = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffDateTime
  );
  const dropoffLocation = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffLocation
  );
  const driverAge = useSelector(
    CarsAvailabilitySelectors.getSearchFormDriverAge
  );

  const handleDropoffLocationChange = useCallback(
    (place: Suggestion) => {
      dispatch(CarsAvailabilityActions.setNewDropoffLocation(place));
      dispatch(CarsAvailabilityActions.setNewDropoffType(DropoffType.OneWay));
    },
    [dispatch]
  );

  const handleDropoffTypeChange = useCallback(
    (dropoffType: DropoffType) => {
      dispatch(CarsAvailabilityActions.setNewDropoffType(dropoffType));
      if (dropoffType === DropoffType.RoundTrip && pickupLocation) {
        dispatch(CarsAvailabilityActions.setNewDropoffLocation(pickupLocation));
      }
    },
    [dispatch, pickupLocation]
  );

  const handlePickupLocationChange = useCallback(
    (place: Suggestion) => {
      dispatch(CarsAvailabilityActions.setNewPickupLocation(place));
      if (dropoffType === DropoffType.RoundTrip) {
        dispatch(CarsAvailabilityActions.setNewDropoffLocation(place));
      }
    },
    [dispatch, dropoffType]
  );

  const handlePickupDateTimeChange = useCallback(
    (date: Date | undefined) => {
      dispatch(
        CarsAvailabilityActions.setNewPickupDateTime(
          date == null ? undefined : date?.toISOString()
        )
      );
    },
    [dispatch]
  );

  const handleDropoffDateTimeChange = useCallback(
    (date: Date | undefined) => {
      dispatch(
        CarsAvailabilityActions.setNewDropoffDateTime(
          date == null ? undefined : date?.toISOString()
        )
      );
    },
    [dispatch]
  );

  const handleDriverAgeChange = useCallback(
    (ageStr: string | undefined) => {
      setAgeError(undefined);
      if (ageStr == null) {
        return;
      }

      const numberStr = ageStr
        // /\uFF10 === full width zero \uFF19 === full width nine
        // Japanese inputs may enter these values and it would be mean if we don't accept them
        .replace(/[\uFF10-\uFF19]/g, (char) =>
          String.fromCharCode(char.charCodeAt(0) - 0xfee0)
        )
        // remove any non integers
        .replace(/[^0-9]/g, "");

      if (numberStr.length === 0) {
        dispatch(CarsAvailabilityActions.setNewDriverAge(undefined));
        setAgeError(CarsSearchErrors.ageRequired);
      } else {
        const age = Number(numberStr);
        // Don't do isDriverAgeValid here because it makes the input user unfriendly
        // by not allowing to delete the numbers (it wouldn't allow you to change the value from 27 to 2)
        if (!isNaN(age)) {
          dispatch(CarsAvailabilityActions.setNewDriverAge(age));
        } else {
          setAgeError(CarsSearchErrors.invalidAge);
        }
      }
    },
    [dispatch]
  );

  const isFormValid = useMemo(() => {
    return (
      pickupDateTime != null &&
      pickupLocation != null &&
      dropoffDateTime != null &&
      dropoffLocation != null &&
      driverAge != null &&
      isDriverAgeValid(driverAge)
    );
  }, [
    pickupDateTime,
    pickupLocation,
    dropoffDateTime,
    dropoffLocation,
    driverAge,
  ]);

  const handleSearch = useCallback(() => {
    if (
      isFormValid &&
      pickupDateTime != null &&
      pickupLocation != null &&
      dropoffDateTime != null &&
      dropoffLocation != null &&
      driverAge != null
    ) {
      dispatch(CarsAvailabilityActions.clearSearchDetails());
      CarNavigationUtility.placeSuggestionToUrl(pickupLocation);

      const pickupLocationCode =
        CarNavigationUtility.placeSuggestionToUrl(pickupLocation);
      const dropoffLocationCode =
        CarNavigationUtility.placeSuggestionToUrl(dropoffLocation);

      const searchParams = CarNavigationUtility.buildSearchParams({
        pickupDateTime,
        dropoffDateTime,
        driverAge,
      });

      navigate(
        `${CARS_AVAILABILITY_PATH}/${pickupLocationCode}/${dropoffLocationCode}?${searchParams.toString()}`
      );
    } else if (!isFormValid) {
      setValidationErrors([
        ...(pickupLocation ? [] : [CarsSearchErrors.pickupRequired]),
        ...(dropoffLocation ? [] : [CarsSearchErrors.dropoffReqired]),
        ...(pickupDateTime ? [] : [CarsSearchErrors.pickupTimeRequired]),
        ...(dropoffDateTime ? [] : [CarsSearchErrors.dropoffTimeRequired]),
        ...(driverAge !== null && driverAge !== undefined && driverAge > 0
          ? []
          : [CarsSearchErrors.ageRequired]),
        ...(driverAge != null && isDriverAgeValid(driverAge)
          ? []
          : [CarsSearchErrors.invalidAge]),
      ]);
    }
  }, [
    isFormValid,
    pickupDateTime,
    pickupLocation,
    dropoffDateTime,
    dropoffLocation,
    driverAge,
    dispatch,
    navigate,
  ]);

  const onFormSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      handleSearch();
      onSubmit && onSubmit();
    },
    [handleSearch, onSubmit]
  );

  return useMemo(
    () => ({
      context: {
        pickupDateTime,
        pickupLocation,
        dropoffDateTime,
        dropoffLocation,
        driverAge,
        dropoffType,
        isFormValid,
        validationErrors,
        ageError,
      },
      handlers: {
        handlePickupDateTimeChange,
        handlePickupLocationChange,
        handleDropoffTypeChange,
        handleDropoffDateTimeChange,
        handleDropoffLocationChange,
        handleDriverAgeChange,
        onFormSubmit,
        validateAge: () => {
          if (driverAge == null || driverAge < MIN_SEARCH_DRIVER_AGE) {
            setAgeError(CarsSearchErrors.invalidAge);
          }
        },
      },
    }),
    [
      pickupDateTime,
      pickupLocation,
      dropoffDateTime,
      dropoffLocation,
      driverAge,
      dropoffType,
      isFormValid,
      ageError,
      handlePickupDateTimeChange,
      handlePickupLocationChange,
      handleDropoffTypeChange,
      handleDropoffDateTimeChange,
      handleDropoffLocationChange,
      handleDriverAgeChange,
      onFormSubmit,
      validationErrors,
    ]
  );
};
